import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { useTheme } from "styled-components";
import penEdit from "../../assets/penEdit.png";
import BookService from "../../services/BookService";
import { getAllAuthors } from "../../services/AuthorService";
import { getAllIllustrators } from "../../services/IllustratorService";
import { getAllPublishers } from "../../services/PublisherService";
import EditBook from "../BookComponents/EditBook";
import EditAuthorModal from "../AuthorComponents/EditAuthorModal";
import EditIllustratorModal from "../IllustratorComponents/EditIllustratorModal";
import EditPublisherModal from "../PublisherComponents/EditPublisherModal";


const PageWrapper = styled.div`
    max-width: 1100px;
    margin: 2rem auto;
    padding: 0 1rem;
`;

const PageTitle = styled.h2`
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
`;

const TabBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    border-bottom: 2px solid ${({ theme }) => theme.colors.lineColor};
    margin-bottom: 1.5rem;
`;

const Tab = styled.button`
    padding: 0.55rem 1.1rem;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text};
    box-shadow: ${({ $active, theme }) => $active ? `inset 0 -2px 0 ${theme.colors.primary}` : "none"};
    white-space: nowrap;
    transition: color 0.15s, box-shadow 0.15s;
    &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const Toolbar = styled.div`
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1rem;
    align-items: center;
`;

const SearchInput = styled.input`
    flex: 1;
    max-width: 320px;
    padding: 0.45rem 0.75rem;
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.9rem;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    outline: none;
    &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Card = styled.div`
    background: ${({ theme }) => theme.colors.card};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.md};
    padding: 1.5rem;
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
`;

const Th = styled.th`
    text-align: left;
    padding: 0.6rem 0.8rem;
    color: ${({ theme }) => theme.colors.lineColor};
    font-weight: 600;
    border-bottom: 2px solid ${({ theme }) => theme.colors.lineColor};
    white-space: nowrap;
`;

const Tr = styled.tr`
    &:not(:last-child) td { border-bottom: 1px solid ${({ theme }) => theme.colors.lineColor}33; }
    &:hover td { background: ${({ theme }) => theme.colors.primary}08; }
`;

const Td = styled.td`
    padding: 0.6rem 0.8rem;
    color: ${({ theme }) => theme.colors.text};
    vertical-align: middle;
`;

const EditBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    transition: background 0.15s;
    line-height: 0;
    &:hover { background: ${({ theme }) => theme.colors.primary}18; }
`;

const EmptyMsg = styled.p`
    text-align: center;
    color: ${({ theme }) => theme.colors.lineColor};
    padding: 2rem 0;
    margin: 0;
`;

const Pagination = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1rem;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text};
`;

const PageBtn = styled.button`
    padding: 0.35rem 0.7rem;
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    border-radius: 4px;
    background: none;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    &:disabled { opacity: 0.4; cursor: default; }
    &:not(:disabled):hover { border-color: ${({ theme }) => theme.colors.primary}; color: ${({ theme }) => theme.colors.primary}; }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0; left: 0;
    width: 100vw; height: 100vh;
    background: rgba(0,0,0,0.5);
    z-index: 1000;
    overflow-y: auto;
    padding: 2rem 1rem;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    box-sizing: border-box;
`;

const ModalCloseBtn = styled.button`
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    border: none;
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    &:hover { opacity: 0.85; }
`;


const PAGE_SIZE = 15;

function useTabData(tab, targetTab, loader) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const loaded = useRef(false);

    useEffect(() => {
        if (tab !== targetTab || loaded.current) return;
        loaded.current = true;
        setLoading(true);
        loader()
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [tab, targetTab, loader]);

    const refresh = useCallback(() => {
        setLoading(true);
        loader()
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [loader]);

    return { data, loading, setData, refresh };
}


function CatalogManagement() {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState("books");

    const [books, setBooks] = useState([]);
    const [booksTotal, setBooksTotal] = useState(0);
    const [booksPage, setBooksPage] = useState(0);
    const [bookQuery, setBookQuery] = useState("");
    const [booksLoading, setBooksLoading] = useState(false);
    const [editBook, setEditBook] = useState(null);
    const [loadingEditBook, setLoadingEditBook] = useState(false);

    const [authorQuery, setAuthorQuery] = useState("");
    const [authorPage, setAuthorPage] = useState(0);
    const [editAuthorId, setEditAuthorId] = useState(null);
    const authorLoader = useCallback(() => getAllAuthors(), []);
    const { data: authors, loading: authorsLoading, refresh: refreshAuthors } = useTabData(activeTab, "authors", authorLoader);

    // Illustrators
    const [illustratorQuery, setIllustratorQuery] = useState("");
    const [illustratorPage, setIllustratorPage] = useState(0);
    const [editIllustratorId, setEditIllustratorId] = useState(null);
    const illustratorLoader = useCallback(() => getAllIllustrators(), []);
    const { data: illustrators, loading: illustratorsLoading, refresh: refreshIllustrators } = useTabData(activeTab, "illustrators", illustratorLoader);

    // Publishers
    const [publisherQuery, setPublisherQuery] = useState("");
    const [publisherPage, setPublisherPage] = useState(0);
    const [editPublisherId, setEditPublisherId] = useState(null);
    const publisherLoader = useCallback(() => getAllPublishers(), []);
    const { data: publishers, loading: publishersLoading, refresh: refreshPublishers } = useTabData(activeTab, "publishers", publisherLoader);

    // ── Books fetch ──────────────────────────────────────────────────────────

    const fetchBooks = useCallback(async (query, page) => {
        setBooksLoading(true);
        try {
            let result;
            if (query.trim()) {
                result = await BookService.searchBooks(query.trim(), "name", page, PAGE_SIZE);
            } else {
                result = await BookService.getBooks(page, PAGE_SIZE);
            }
            setBooks(result.content || []);
            setBooksTotal(result.totalElements || 0);
        } catch {
            setBooks([]);
        } finally {
            setBooksLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === "books") fetchBooks(bookQuery, booksPage);
    }, [activeTab, bookQuery, booksPage, fetchBooks]);

    const handleBookSearch = (e) => {
        setBookQuery(e.target.value);
        setBooksPage(0);
    };

    const handleEditBookClick = async (bookId) => {
        setLoadingEditBook(true);
        try {
            const full = await BookService.getBookById(bookId);
            setEditBook(full);
        } catch {
            // silent
        } finally {
            setLoadingEditBook(false);
        }
    };

    const booksTotalPages = Math.ceil(booksTotal / PAGE_SIZE);

    // ── Filtered + paginated lists ───────────────────────────────────────────

    const filteredAuthors = authors.filter(a =>
        `${a.name} ${a.surname}`.toLowerCase().includes(authorQuery.toLowerCase())
    );
    const pagedAuthors = filteredAuthors.slice(authorPage * PAGE_SIZE, (authorPage + 1) * PAGE_SIZE);
    const authorsTotalPages = Math.ceil(filteredAuthors.length / PAGE_SIZE);

    const filteredIllustrators = illustrators.filter(i =>
        `${i.name} ${i.surname}`.toLowerCase().includes(illustratorQuery.toLowerCase())
    );
    const pagedIllustrators = filteredIllustrators.slice(illustratorPage * PAGE_SIZE, (illustratorPage + 1) * PAGE_SIZE);
    const illustratorsTotalPages = Math.ceil(filteredIllustrators.length / PAGE_SIZE);

    const filteredPublishers = publishers.filter(p =>
        (p.name || "").toLowerCase().includes(publisherQuery.toLowerCase())
    );
    const pagedPublishers = filteredPublishers.slice(publisherPage * PAGE_SIZE, (publisherPage + 1) * PAGE_SIZE);
    const publishersTotalPages = Math.ceil(filteredPublishers.length / PAGE_SIZE);

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <PageWrapper>
            <PageTitle>Katalog</PageTitle>

            <TabBar>
                {[
                    { id: "books",       label: "Knjige" },
                    { id: "authors",     label: "Autori" },
                    { id: "illustrators",label: "Ilustratori" },
                    { id: "publishers",  label: "Izdavači" },
                ].map(t => (
                    <Tab key={t.id} $active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
                        {t.label}
                    </Tab>
                ))}
            </TabBar>

            {/* ── BOOKS ── */}
            {activeTab === "books" && (
                <Card>
                    <Toolbar>
                        <SearchInput
                            placeholder="Pretraži po nazivu..."
                            value={bookQuery}
                            onChange={handleBookSearch}
                        />
                    </Toolbar>
                    {booksLoading ? <EmptyMsg>Učitavanje...</EmptyMsg> : books.length === 0 ? (
                        <EmptyMsg>Nema knjiga.</EmptyMsg>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Naziv</Th>
                                    <Th>Autori</Th>
                                    <Th>Žanr</Th>
                                    <Th>Dostupno</Th>
                                    <Th></Th>
                                </tr>
                            </thead>
                            <tbody>
                                {books.map(b => (
                                    <Tr key={b.id}>
                                        <Td>{b.name}</Td>
                                        <Td>{b.authors?.map(a => `${a.name} ${a.surname}`).join(", ") || "—"}</Td>
                                        <Td>{b.genre || "—"}</Td>
                                        <Td>{b.available ?? "—"}</Td>
                                        <Td>
                                            <EditBtn
                                                title="Uredi knjigu"
                                                onClick={() => handleEditBookClick(b.id)}
                                                disabled={loadingEditBook}
                                            >
                                                <img src={penEdit} alt="Uredi" style={{ width: 16, height: 16 }} />
                                            </EditBtn>
                                        </Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    {booksTotalPages > 1 && (
                        <Pagination>
                            <PageBtn disabled={booksPage === 0} onClick={() => setBooksPage(p => p - 1)}>‹</PageBtn>
                            <span>{booksPage + 1} / {booksTotalPages}</span>
                            <PageBtn disabled={booksPage >= booksTotalPages - 1} onClick={() => setBooksPage(p => p + 1)}>›</PageBtn>
                        </Pagination>
                    )}
                </Card>
            )}

            {/* ── AUTHORS ── */}
            {activeTab === "authors" && (
                <Card>
                    <Toolbar>
                        <SearchInput
                            placeholder="Pretraži autore..."
                            value={authorQuery}
                            onChange={e => { setAuthorQuery(e.target.value); setAuthorPage(0); }}
                        />
                    </Toolbar>
                    {authorsLoading ? <EmptyMsg>Učitavanje...</EmptyMsg> : filteredAuthors.length === 0 ? (
                        <EmptyMsg>Nema autora.</EmptyMsg>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Ime</Th>
                                    <Th>Prezime</Th>
                                    <Th></Th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedAuthors.map(a => (
                                    <Tr key={a.id}>
                                        <Td>{a.name}</Td>
                                        <Td>{a.surname}</Td>
                                        <Td>
                                            <EditBtn title="Uredi autora" onClick={() => setEditAuthorId(a.id)}>
                                                <img src={penEdit} alt="Uredi" style={{ width: 16, height: 16 }} />
                                            </EditBtn>
                                        </Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    {authorsTotalPages > 1 && (
                        <Pagination>
                            <PageBtn disabled={authorPage === 0} onClick={() => setAuthorPage(p => p - 1)}>‹</PageBtn>
                            <span>{authorPage + 1} / {authorsTotalPages}</span>
                            <PageBtn disabled={authorPage >= authorsTotalPages - 1} onClick={() => setAuthorPage(p => p + 1)}>›</PageBtn>
                        </Pagination>
                    )}
                </Card>
            )}

            {/* ── ILLUSTRATORS ── */}
            {activeTab === "illustrators" && (
                <Card>
                    <Toolbar>
                        <SearchInput
                            placeholder="Pretraži ilustratore..."
                            value={illustratorQuery}
                            onChange={e => { setIllustratorQuery(e.target.value); setIllustratorPage(0); }}
                        />
                    </Toolbar>
                    {illustratorsLoading ? <EmptyMsg>Učitavanje...</EmptyMsg> : filteredIllustrators.length === 0 ? (
                        <EmptyMsg>Nema ilustratora.</EmptyMsg>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Ime</Th>
                                    <Th>Prezime</Th>
                                    <Th></Th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedIllustrators.map(i => (
                                    <Tr key={i.id}>
                                        <Td>{i.name}</Td>
                                        <Td>{i.surname}</Td>
                                        <Td>
                                            <EditBtn title="Uredi ilustratora" onClick={() => setEditIllustratorId(i.id)}>
                                                <img src={penEdit} alt="Uredi" style={{ width: 16, height: 16 }} />
                                            </EditBtn>
                                        </Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    {illustratorsTotalPages > 1 && (
                        <Pagination>
                            <PageBtn disabled={illustratorPage === 0} onClick={() => setIllustratorPage(p => p - 1)}>‹</PageBtn>
                            <span>{illustratorPage + 1} / {illustratorsTotalPages}</span>
                            <PageBtn disabled={illustratorPage >= illustratorsTotalPages - 1} onClick={() => setIllustratorPage(p => p + 1)}>›</PageBtn>
                        </Pagination>
                    )}
                </Card>
            )}

            {/* ── PUBLISHERS ── */}
            {activeTab === "publishers" && (
                <Card>
                    <Toolbar>
                        <SearchInput
                            placeholder="Pretraži izdavače..."
                            value={publisherQuery}
                            onChange={e => { setPublisherQuery(e.target.value); setPublisherPage(0); }}
                        />
                    </Toolbar>
                    {publishersLoading ? <EmptyMsg>Učitavanje...</EmptyMsg> : filteredPublishers.length === 0 ? (
                        <EmptyMsg>Nema izdavača.</EmptyMsg>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Naziv</Th>
                                    <Th>Adresa</Th>
                                    <Th>Web</Th>
                                    <Th></Th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedPublishers.map(p => (
                                    <Tr key={p.id}>
                                        <Td>{p.name}</Td>
                                        <Td>{p.address || "—"}</Td>
                                        <Td>{p.webSite || "—"}</Td>
                                        <Td>
                                            <EditBtn title="Uredi izdavača" onClick={() => setEditPublisherId(p.id)}>
                                                <img src={penEdit} alt="Uredi" style={{ width: 16, height: 16 }} />
                                            </EditBtn>
                                        </Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    {publishersTotalPages > 1 && (
                        <Pagination>
                            <PageBtn disabled={publisherPage === 0} onClick={() => setPublisherPage(p => p - 1)}>‹</PageBtn>
                            <span>{publisherPage + 1} / {publishersTotalPages}</span>
                            <PageBtn disabled={publisherPage >= publishersTotalPages - 1} onClick={() => setPublisherPage(p => p + 1)}>›</PageBtn>
                        </Pagination>
                    )}
                </Card>
            )}

            {/* ── EDIT BOOK MODAL ── */}
            {editBook && (
                <ModalOverlay onClick={() => setEditBook(null)}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: "relative", width: "fit-content" }}
                    >
                        <ModalCloseBtn onClick={() => setEditBook(null)} title="Zatvori" style={{ position: "absolute", top: "0.75rem", right: "0.75rem", zIndex: 1 }}>×</ModalCloseBtn>
                        <EditBook
                            book={editBook}
                            onSaved={() => {
                                setEditBook(null);
                                fetchBooks(bookQuery, booksPage);
                            }}
                            onCancel={() => setEditBook(null)}
                        />
                    </div>
                </ModalOverlay>
            )}

            {/* ── EDIT AUTHOR MODAL ── */}
            <EditAuthorModal
                isOpen={!!editAuthorId}
                id={editAuthorId}
                onClose={() => setEditAuthorId(null)}
                onAuthorUpdated={() => { setEditAuthorId(null); refreshAuthors(); }}
            />

            {/* ── EDIT ILLUSTRATOR MODAL ── */}
            <EditIllustratorModal
                isOpen={!!editIllustratorId}
                id={editIllustratorId}
                onClose={() => setEditIllustratorId(null)}
                onIllustratorUpdated={() => { setEditIllustratorId(null); refreshIllustrators(); }}
            />

            {/* ── EDIT PUBLISHER MODAL ── */}
            <EditPublisherModal
                isOpen={!!editPublisherId}
                id={editPublisherId}
                onClose={() => setEditPublisherId(null)}
                onPublisherUpdated={() => { setEditPublisherId(null); refreshPublishers(); }}
            />
        </PageWrapper>
    );
}

export default CatalogManagement;
